import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { GridSortModel } from '@mui/x-data-grid';
import toast from 'react-hot-toast';
import { debounce } from 'ts-debounce';
import { useDropzone } from 'react-dropzone';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { useDropzoneStyles } from 'src/components/AttachFileUrlModal/useDropzoneStyles';
import {
  useAttachDocumentToCompanyApi,
  useDeleteCompanyDocumentApi,
  useGetAdminCompanyDocumentsApi,
} from '../api';
import {
  CompanyDocument,
  CompanyDocumentsTabProps,
  CompanyDocumentsTabViewModel,
  GetAdminCompanyDocumentsParams,
} from '../types';

const getServerSortField = (sort?: string) => {
  if (!sort) return undefined;

  const sortMap: Record<string, string> = {
    dateCreated: 'date_created',
    documentType: 'type',
    fileName: 'name',
  };

  const [field, order] = sort.split('_');
  const serverField = sortMap[field] || field;
  return `${serverField}_${order}`;
};

const getDocumentTypeLabel = (documentType: string) => {
  switch (documentType.toLowerCase()) {
    case 'w9':
      return 'W9';
    case 'loa':
      return 'LOA';
    default: {
      const words = documentType.toLowerCase().split('_');
      return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
  }
};

export const useCompanyDocumentsTabController = (
  params: CompanyDocumentsTabProps,
): CompanyDocumentsTabViewModel => {
  const { companyId, company } = params;

  const [documents, setDocuments] = useState<CompanyDocument[]>([]);
  const [documentsCount, setDocumentsCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocumentType, setUploadDocumentType] = useState('');
  const [documentTypeError, setDocumentTypeError] = useState('');
  const [previewDocument, setPreviewDocument] = useState<CompanyDocument | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [sort, setSort] = useState<string>('');
  const [showDocumentTypeDialog, setShowDocumentTypeDialog] = useState<boolean>(false);
  const [signLoaOpen, setSignLoaOpen] = useState<boolean>(false);

  // Refs to store current values for debounced function
  const searchQueryRef = useRef(searchQuery);
  const pageRef = useRef(page);
  const pageSizeRef = useRef(pageSize);
  const sortRef = useRef(sort);

  // Update refs when state changes
  useEffect(() => {
    searchQueryRef.current = searchQuery;
  }, [searchQuery]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    sortRef.current = sort;
  }, [sort]);

  const getCompanyDocuments = useGetAdminCompanyDocumentsApi();
  const attachDocument = useAttachDocumentToCompanyApi();
  const deleteDocument = useDeleteCompanyDocumentApi();

  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const reloadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params: GetAdminCompanyDocumentsParams = {
        limit: pageSizeRef.current,
        offset: pageRef.current * pageSizeRef.current,
      };

      // Only include query parameter if it's not empty
      if (searchQueryRef.current.trim()) {
        params.query = searchQueryRef.current;
      }

      // Only include sort parameter if it's not empty
      if (sortRef.current.trim()) {
        params.sort = getServerSortField(sortRef.current);
      }

      const response = await getCompanyDocuments(companyId, params);
      setDocuments(response.data?.items || []);
      setDocumentsCount(response.data?.count || 0);
    } catch (error) {
      console.error('Failed to reload documents:', error);
      toast.error('Failed to reload documents');
    } finally {
      setLoading(false);
    }
  }, [companyId, getCompanyDocuments]);

  const changeSorting = useCallback((s: GridSortModel) => {
    if (s.length > 0) {
      setSort(`${s[0].field}_${s[0].sort}`);
    } else {
      setSort('');
    }
  }, []);

  const reloadDocumentsDebounce = useMemo(() => debounce(reloadDocuments, 500), []);

  useEffect(() => {
    reloadDocuments();
  }, [page, sort, reloadDocuments]);

  useEffect(() => {
    setPage(0);
  }, [sort]);

  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      setUploadFile(file);

      // Check if file is not PDF and document type is not Custom
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (fileExtension !== 'pdf' && uploadDocumentType && uploadDocumentType !== 'custom') {
        setDocumentTypeError('Only PDF files are allowed for this document type. Please select Custom for other file types.');
      } else {
        setDocumentTypeError('');
      }
    }
  };

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    onDrop: (files) => {
      handleFileSelect(files);
      if (!uploadDocumentType) {
        setDocumentTypeError('Document Type is required');
      } else {
        setDocumentTypeError('');
      }
    },
    accept: uploadDocumentType === 'custom' ? undefined : {
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
  });

  const dropzoneStyle = useDropzoneStyles({ isFocused, isDragAccept, isDragReject });

  const resetUploadState = useCallback(() => {
    setUploadModalOpen(false);
    setUploadFile(null);
    setUploadDocumentType('');
    setDocumentTypeError('');
  }, []);

  const onUpload = async () => {
    if (!uploadFile || !uploadDocumentType) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('documentType', uploadDocumentType);

      await attachDocument(companyId, formData);
      toast.success('Document uploaded successfully');
      resetUploadState();
      reloadDocuments();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const onDelete = (document: CompanyDocument) => {
    openDialog({
      title: `Delete "${document.fileName}"`,
      content: 'Are you sure you want to delete this document? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteDocument(companyId, document.id);
          toast.success('Document deleted successfully');
          reloadDocuments();
        } catch (error) {
          console.error('Delete failed:', error);
          toast.error('Failed to delete document');
        }
      },
    });
  };

  const onPreview = (document: CompanyDocument) => {
    setPreviewDocument(document);
  };

  const onClosePreview = () => {
    setPreviewDocument(null);
  };

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when searching
    reloadDocumentsDebounce();
  };

  const onPaginationChange = (paginationModel: { page: number; pageSize: number }) => {
    setPage(paginationModel.page);
    setPageSize(paginationModel.pageSize);
    reloadDocuments();
  };

  const onChangeUploadDocumentType = (event: SelectChangeEvent<string>) => {
    setUploadDocumentType(event.target.value);
    // Re-validate the selected file when document type changes
    if (uploadFile) {
      const fileExtension = uploadFile.name.split('.').pop()?.toLowerCase();
      if (fileExtension !== 'pdf' && event.target.value && event.target.value !== 'custom') {
        setDocumentTypeError('Only PDF files are allowed for this document type. Please select Custom for others.');
      } else {
        setDocumentTypeError('');
      }
    } else {
      setDocumentTypeError('');
    }
  };

  return {
    company,
    documents,
    documentsCount,
    loading,
    uploading,
    uploadModalOpen,
    uploadFile,
    uploadDocumentType,
    documentTypeError,
    previewDocument,
    searchQuery,
    page,
    pageSize,
    sort,
    showDocumentTypeDialog,
    signLoaOpen,
    dropzoneStyle,
    getRootProps,
    getInputProps,
    ConfirmationDialog,
    getDocumentTypeLabel,
    reloadDocuments,
    changeSorting,
    onSearchChange,
    onPaginationChange,
    onOpenUploadModal: () => setUploadModalOpen(true),
    onChangeUploadDocumentType,
    onUpload,
    resetUploadState,
    onDelete,
    onPreview,
    onClosePreview,
    onOpenSignLoa: () => setSignLoaOpen(true),
    onCloseSignLoa: () => setSignLoaOpen(false),
    onCloseDocumentTypeDialog: () => setShowDocumentTypeDialog(false),
  };
};
